import { useEffect, useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import axios from 'axios';
import { ArrowLeft, Send, MapPin, Phone, Mail } from 'lucide-react';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Textarea } from '../../components/ui/textarea';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const StudioContactPage = () => {
  const { slug } = useParams();
  const [studio, setStudio] = useState(null);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    message: ''
  });

  useEffect(() => {
    const fetchStudio = async () => {
      try {
        const response = await axios.get(`${API}/studios/public/${slug}`);
        setStudio(response.data);
      } catch (err) {
        console.error(err);
      } finally {
        setLoading(false);
      }
    };
    fetchStudio();
  }, [slug]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);

    try {
      await axios.post(`${API}/leads`, {
        studio_id: studio.id,
        name: formData.name,
        email: formData.email,
        phone: formData.phone || null,
        message: formData.message || null
      });
      
      toast.success('Message sent! We\'ll be in touch soon.');
      setFormData({ name: '', email: '', phone: '', message: '' });
    } catch (error) {
      toast.error('Failed to send message. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-[#050505] flex items-center justify-center">
        <div className="spinner w-10 h-10"></div>
      </div>
    );
  }

  if (!studio) {
    return (
      <div className="min-h-screen bg-[#050505] flex items-center justify-center">
        <div className="text-center">
          <h1 className="text-2xl font-bold mb-4">Studio Not Found</h1>
          <Link to="/" className="text-[#F5D061] hover:underline">Go back home</Link>
        </div>
      </div>
    );
  }

  const accentColor = studio.accent_color || '#FFFFFF';

  return (
    <div className="min-h-screen bg-[#050505]" data-testid="contact-page">
      {/* Header */}
      <header className="fixed top-0 left-0 right-0 z-50 glass">
        <div className="max-w-7xl mx-auto px-6 lg:px-12">
          <div className="flex items-center justify-between h-16">
            <Link 
              to={`/s/${slug}`}
              className="flex items-center gap-2 text-white/60 hover:text-white"
            >
              <ArrowLeft size={18} />
              <span className="font-bold" style={{ color: accentColor }}>{studio.name}</span>
            </Link>
          </div>
        </div>
      </header>

      <main className="pt-24 pb-16 px-6 lg:px-12">
        <div className="max-w-5xl mx-auto">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
          >
            <h1 className="text-4xl font-bold mb-4">Contact Us</h1>
            <p className="text-white/60 mb-12">Get in touch for a quote or any questions</p>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-12">
              {/* Contact Form */}
              <div className="bg-[#0F0F0F] border border-white/10 rounded-sm p-8">
                <form onSubmit={handleSubmit} className="space-y-6">
                  <div>
                    <Label htmlFor="name" className="text-white/70 mb-2 block">Name *</Label>
                    <Input
                      id="name"
                      type="text"
                      value={formData.name}
                      onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                      placeholder="Your name"
                      className="bg-[#171717] border-white/10 h-12"
                      style={{ '--tw-ring-color': accentColor }}
                      data-testid="contact-name"
                      required
                    />
                  </div>

                  <div>
                    <Label htmlFor="email" className="text-white/70 mb-2 block">Email *</Label>
                    <Input
                      id="email"
                      type="email"
                      value={formData.email}
                      onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                      placeholder="your@email.com"
                      className="bg-[#171717] border-white/10 h-12"
                      data-testid="contact-email"
                      required
                    />
                  </div>

                  <div>
                    <Label htmlFor="phone" className="text-white/70 mb-2 block">Phone</Label>
                    <Input
                      id="phone"
                      type="tel"
                      value={formData.phone}
                      onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                      placeholder="+1 234 567 890"
                      className="bg-[#171717] border-white/10 h-12"
                      data-testid="contact-phone"
                    />
                  </div>

                  <div>
                    <Label htmlFor="message" className="text-white/70 mb-2 block">Message</Label>
                    <Textarea
                      id="message"
                      value={formData.message}
                      onChange={(e) => setFormData({ ...formData, message: e.target.value })}
                      placeholder="Tell us about your project..."
                      className="bg-[#171717] border-white/10 min-h-[120px]"
                      data-testid="contact-message"
                    />
                  </div>

                  <Button
                    type="submit"
                    disabled={submitting}
                    className="w-full font-bold uppercase tracking-wider py-6"
                    style={{ backgroundColor: accentColor, color: '#000' }}
                    data-testid="contact-submit"
                  >
                    {submitting ? (
                      <span className="spinner w-5 h-5"></span>
                    ) : (
                      <>
                        Send Message
                        <Send className="ml-2 w-4 h-4" />
                      </>
                    )}
                  </Button>
                </form>
              </div>

              {/* Contact Info */}
              <div className="space-y-8">
                <div>
                  <h2 className="text-2xl font-bold mb-6">Get in Touch</h2>
                  <p className="text-white/60 mb-8">
                    We'd love to hear from you. Send us a message and we'll respond as soon as possible.
                  </p>
                </div>

                <div className="space-y-6">
                  {studio.address && (
                    <div className="flex items-start gap-4">
                      <div 
                        className="w-10 h-10 rounded-sm flex items-center justify-center"
                        style={{ backgroundColor: `${accentColor}20` }}
                      >
                        <MapPin size={18} style={{ color: accentColor }} />
                      </div>
                      <div>
                        <p className="font-semibold mb-1">Address</p>
                        <p className="text-white/60">{studio.address}</p>
                      </div>
                    </div>
                  )}

                  {studio.phone && (
                    <div className="flex items-start gap-4">
                      <div 
                        className="w-10 h-10 rounded-sm flex items-center justify-center"
                        style={{ backgroundColor: `${accentColor}20` }}
                      >
                        <Phone size={18} style={{ color: accentColor }} />
                      </div>
                      <div>
                        <p className="font-semibold mb-1">Phone</p>
                        <a href={`tel:${studio.phone}`} className="text-white/60 hover:text-white">
                          {studio.phone}
                        </a>
                      </div>
                    </div>
                  )}

                  {studio.email && (
                    <div className="flex items-start gap-4">
                      <div 
                        className="w-10 h-10 rounded-sm flex items-center justify-center"
                        style={{ backgroundColor: `${accentColor}20` }}
                      >
                        <Mail size={18} style={{ color: accentColor }} />
                      </div>
                      <div>
                        <p className="font-semibold mb-1">Email</p>
                        <a href={`mailto:${studio.email}`} className="text-white/60 hover:text-white">
                          {studio.email}
                        </a>
                      </div>
                    </div>
                  )}
                </div>

                {/* Try Visualizer CTA */}
                <div className="bg-[#171717] border border-white/5 rounded-sm p-6 mt-8">
                  <p className="text-white/70 mb-4">
                    Want to see how a wrap would look on your car before committing?
                  </p>
                  <Link to={`/s/${slug}/visualize`}>
                    <Button 
                      variant="outline"
                      className="border-white/20 text-white hover:bg-white/5"
                    >
                      Try Our Visualizer
                    </Button>
                  </Link>
                </div>
              </div>
            </div>
          </motion.div>
        </div>
      </main>
    </div>
  );
};

export default StudioContactPage;
